import { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const ADMIN_EMAIL = "myimprovereview@gmail.com";

const PRIORITY_COLORS = {
  red: { bg: "#fff0f0", border: "#ffcccc", badge: "#ff4444", label: "1-2 Stars" },
  yellow: { bg: "#fffbf0", border: "#ffe599", badge: "#f5a623", label: "3 Stars" },
  green: { bg: "#f0fff4", border: "#b2f5c8", badge: "#22c55e", label: "4-5 Stars" },
};

const TIME_FILTERS = [
  { label: "This Week", days: 7 },
  { label: "This Month", days: 30 },
  { label: "Last 3 Months", days: 90 },
  { label: "All Time", days: 9999 },
];

const THRESHOLDS = {
  noLoginDays: 7,
  pendingReviewsLimit: 7,
  conversionRateMin: 25,
  csvUploadDays: 7,
};

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin() {
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError("Invalid email or password. Please try again.");
    else onLogin();
    setLoading(false);
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0f0f0f", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif" }}>
      <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "48px 40px", width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h1 style={{ color: "#f5f0e8", fontSize: 24, fontWeight: 700, margin: "0 0 8px" }}>Improve Review</h1>
          <p style={{ color: "#888", fontSize: 14, fontStyle: "italic", margin: 0 }}>Sign in to your dashboard</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} placeholder="you@yourbusiness.com"
              style={{ width: "100%", background: "#111", border: "1px solid #2a2a2a", borderRadius: 4, padding: "10px 14px", color: "#f5f0e8", fontSize: 14, fontFamily: "Georgia, serif", outline: "none", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} placeholder="••••••••"
              style={{ width: "100%", background: "#111", border: "1px solid #2a2a2a", borderRadius: 4, padding: "10px 14px", color: "#f5f0e8", fontSize: 14, fontFamily: "Georgia, serif", outline: "none", boxSizing: "border-box" }} />
          </div>
          {error && <p style={{ color: "#ff4444", fontSize: 13, fontStyle: "italic", margin: 0 }}>{error}</p>}
          <button onClick={handleLogin} disabled={loading}
            style={{ background: "#f4e04d", color: "#0f0f0f", border: "none", borderRadius: 4, padding: "12px", fontFamily: "monospace", fontWeight: 700, fontSize: 14, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, marginTop: 8 }}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
        <p style={{ textAlign: "center", color: "#555", fontSize: 12, marginTop: 16, fontStyle: "italic" }}>
          <span onClick={async () => {
            if (!email) { setError("Enter your email address first then click Forgot Password."); return; }
            const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
            if (error) setError("Could not send reset email. Please try again.");
            else { setError(""); alert("Password reset email sent — check your inbox."); }
          }} style={{ color: "#888", cursor: "pointer", textDecoration: "underline" }}>Forgot password?</span>
        </p>
        <p style={{ textAlign: "center", color: "#555", fontSize: 12, marginTop: 8, fontStyle: "italic" }}>Need access? Contact hello@improvereview.net</p>
      </div>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => { fetchClients(); }, []);

  async function fetchClients() {
    setLoading(true);
    const { data: businesses } = await supabase.from("businesses").select("*").order("created_at", { ascending: false });
    if (!businesses) { setLoading(false); return; }

    const clientData = await Promise.all(businesses.map(async (business) => {
      const { data: locations } = await supabase.from("locations").select("id").eq("business_id", business.id);
      const locationIds = locations?.map(l => l.id) || [];

      const { data: reviews } = await supabase.from("reviews").select("*, replies(*)").in("location_id", locationIds.length ? locationIds : ["none"]);
      const { data: requests } = await supabase.from("review_requests").select("*").in("location_id", locationIds.length ? locationIds : ["none"]);

      const pending = reviews?.filter(r => !r.replies?.[0] || r.replies?.[0]?.status !== "approved").length || 0;
      const approved = reviews?.filter(r => r.replies?.[0]?.status === "approved").length || 0;
      const totalReviews = reviews?.length || 0;
      const totalRequests = requests?.length || 0;
      const conversionRate = totalRequests > 0 ? Math.round((totalReviews / totalRequests) * 100) : 0;
      const avgRating = totalReviews > 0 ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / totalReviews).toFixed(1) : "—";

      const lastRequest = requests?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      const daysSinceCSV = lastRequest ? Math.floor((new Date() - new Date(lastRequest.created_at)) / (1000 * 60 * 60 * 24)) : 999;

      const alerts = [];
      if (pending >= THRESHOLDS.pendingReviewsLimit) alerts.push(`${pending} pending reviews`);
      if (conversionRate < THRESHOLDS.conversionRateMin && totalRequests > 0) alerts.push(`${conversionRate}% conversion rate`);
      if (daysSinceCSV >= THRESHOLDS.csvUploadDays) alerts.push(`No CSV in ${daysSinceCSV === 999 ? "ever" : daysSinceCSV + " days"}`);

      const health = alerts.length >= 2 ? "red" : alerts.length === 1 ? "yellow" : "green";

      return { ...business, pending, approved, totalReviews, totalRequests, conversionRate, avgRating, daysSinceCSV, alerts, health, reviews, requests };
    }));

    setClients(clientData);
    setLoading(false);
  }

  const healthColors = { green: "#22c55e", yellow: "#f5a623", red: "#ff4444" };
  const healthLabels = { green: "Healthy", yellow: "Needs Attention", red: "At Risk" };

  if (selectedClient) {
    return (
      <div style={{ fontFamily: "Georgia, serif", minHeight: "100vh", background: "#f9f9f7" }}>
        <div style={{ background: "#0f0f0f", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button onClick={() => setSelectedClient(null)} style={{ background: "transparent", border: "1px solid #444", color: "#888", borderRadius: 4, padding: "4px 12px", fontSize: 12, fontFamily: "monospace", cursor: "pointer" }}>← Back</button>
            <h1 style={{ color: "#f5f0e8", fontSize: 18, margin: 0, fontWeight: 700 }}>{selectedClient.name}</h1>
          </div>
          <button onClick={onLogout} style={{ background: "transparent", border: "1px solid #444", color: "#888", borderRadius: 4, padding: "4px 12px", fontSize: 12, fontFamily: "monospace", cursor: "pointer" }}>Sign Out</button>
        </div>
        <div style={{ padding: "24px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 32 }}>
            {[
              { label: "Total Reviews", value: selectedClient.totalReviews, color: "#0f0f0f" },
              { label: "Avg Rating", value: `${selectedClient.avgRating}★`, color: "#f5a623" },
              { label: "Pending Replies", value: selectedClient.pending, color: selectedClient.pending >= 7 ? "#ff4444" : "#22c55e" },
              { label: "Requests Sent", value: selectedClient.totalRequests, color: "#4f46e5" },
              { label: "Conversion Rate", value: `${selectedClient.conversionRate}%`, color: selectedClient.conversionRate < 25 ? "#ff4444" : "#22c55e" },
            ].map(stat => (
              <div key={stat.label} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: "20px 24px" }}>
                <div style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: "#888", textTransform: "uppercase", marginBottom: 8 }}>{stat.label}</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: stat.color }}>{stat.value}</div>
              </div>
            ))}
          </div>

          {selectedClient.alerts.length > 0 && (
            <div style={{ background: "#fff0f0", border: "1px solid #ffcccc", borderRadius: 8, padding: 20, marginBottom: 24 }}>
              <p style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 700, color: "#ff4444", textTransform: "uppercase", margin: "0 0 12px" }}>⚠ Alerts</p>
              {selectedClient.alerts.map((alert, i) => (
                <p key={i} style={{ fontSize: 14, color: "#333", margin: "4px 0" }}>• {alert}</p>
              ))}
            </div>
          )}

          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 24 }}>
            <p style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 700, color: "#555", textTransform: "uppercase", marginBottom: 16 }}>Recent Reviews</p>
            {selectedClient.reviews?.slice(0, 5).map(review => (
              <div key={review.id} style={{ padding: "12px 0", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{review.reviewer_name}</span>
                  <span style={{ fontSize: 13, color: "#888" }}>{"⭐".repeat(review.rating)}</span>
                </div>
                <p style={{ fontSize: 13, color: "#666", fontStyle: "italic", margin: 0 }}>{review.review_text?.slice(0, 120)}...</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Georgia, serif", minHeight: "100vh", background: "#f9f9f7" }}>
      <div style={{ background: "#0f0f0f", padding: "20px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ color: "#f5f0e8", fontSize: 22, margin: 0, fontWeight: 700 }}>Improve Review</h1>
          <p style={{ color: "#f4e04d", fontSize: 12, margin: "2px 0 0", fontFamily: "monospace", fontWeight: 700 }}>ADMIN DASHBOARD</p>
        </div>
        <button onClick={onLogout} style={{ background: "transparent", border: "1px solid #444", color: "#888", borderRadius: 4, padding: "4px 12px", fontSize: 12, fontFamily: "monospace", cursor: "pointer" }}>Sign Out</button>
      </div>

      <div style={{ padding: "24px 32px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 32 }}>
          {[
            { label: "Total Clients", value: clients.length, color: "#0f0f0f" },
            { label: "Healthy", value: clients.filter(c => c.health === "green").length, color: "#22c55e" },
            { label: "Needs Attention", value: clients.filter(c => c.health === "yellow").length, color: "#f5a623" },
            { label: "At Risk", value: clients.filter(c => c.health === "red").length, color: "#ff4444" },
            { label: "Total Reviews", value: clients.reduce((sum, c) => sum + c.totalReviews, 0), color: "#4f46e5" },
          ].map(stat => (
            <div key={stat.label} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: "20px 24px" }}>
              <div style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: "#888", textTransform: "uppercase", marginBottom: 8 }}>{stat.label}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: stat.color }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {loading && <p style={{ color: "#888", fontStyle: "italic" }}>Loading clients...</p>}

        {!loading && clients.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa" }}>
            <p style={{ fontSize: 18 }}>No clients yet</p>
            <p style={{ fontSize: 14 }}>Clients will appear here once they are onboarded</p>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {clients.map(client => (
            <div key={client.id} onClick={() => setSelectedClient(client)}
              style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: "20px 24px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: healthColors[client.health], flexShrink: 0 }} />
                <div>
                  <p style={{ fontWeight: 700, fontSize: 16, margin: "0 0 2px" }}>{client.name}</p>
                  <p style={{ fontSize: 12, color: "#888", fontFamily: "monospace", margin: 0 }}>{client.email} · {client.plan} plan</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#0f0f0f" }}>{client.totalReviews}</div>
                  <div style={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>REVIEWS</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: client.pending >= 7 ? "#ff4444" : "#0f0f0f" }}>{client.pending}</div>
                  <div style={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>PENDING</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: client.conversionRate < 25 && client.totalRequests > 0 ? "#ff4444" : "#0f0f0f" }}>{client.conversionRate}%</div>
                  <div style={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>CONVERSION</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ background: healthColors[client.health], color: "#fff", borderRadius: 3, padding: "3px 10px", fontSize: 11, fontFamily: "monospace", fontWeight: 700 }}>
                    {healthLabels[client.health]}
                  </span>
                  {client.alerts.length > 0 && (
                    <span style={{ background: "#fff0f0", color: "#ff4444", borderRadius: 3, padding: "3px 10px", fontSize: 11, fontFamily: "monospace", fontWeight: 700 }}>
                      {client.alerts.length} ALERT{client.alerts.length > 1 ? "S" : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(null);
  const [tab, setTab] = useState("reviews");
  const [csvData, setCsvData] = useState([]);
  const [csvStatus, setCsvStatus] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [googleLink, setGoogleLink] = useState("");
  const [sending, setSending] = useState(false);
  const [timeFilter, setTimeFilter] = useState(30);

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) setIsAdmin(session.user.email === ADMIN_EMAIL);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) setIsAdmin(session.user.email === ADMIN_EMAIL);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session && !isAdmin) fetchData();
  }, [session, isAdmin]);

  async function fetchData() {
    setLoading(true);
    const [{ data: reviewData }, { data: requestData }] = await Promise.all([
      supabase.from("reviews").select("*, replies(*)").order("created_at", { ascending: false }),
      supabase.from("review_requests").select("*").order("created_at", { ascending: false }),
    ]);
    setReviews(reviewData || []);
    setRequests(requestData || []);
    setLoading(false);
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    setReviews([]);
    setRequests([]);
    setIsAdmin(false);
  }

  async function updateReply(replyId, text) {
    await supabase.from("replies").update({ edited_reply: text }).eq("id", replyId);
  }

  async function approveReply(replyId) {
    await supabase.from("replies").update({ status: "approved", approved_at: new Date().toISOString() }).eq("id", replyId);
    fetchData();
  }

  function copyToClipboard(text, id) {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  function handleCSV(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const lines = evt.target.result.split("\n").filter(Boolean);
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const rows = lines.slice(1).map((line) => {
        const values = line.split(",").map((v) => v.trim());
        const row = {};
        headers.forEach((h, i) => (row[h] = values[i] || ""));
        return row;
      });
      setCsvData(rows);
      setCsvStatus(`${rows.length} customers loaded. Ready to send.`);
    };
    reader.readAsText(file);
  }

  async function sendRequests() {
    if (!csvData.length || !businessName || !googleLink) {
      setCsvStatus("Please fill in business name, Google review link, and upload a CSV.");
      return;
    }
    setSending(true);
    setCsvStatus("Sending emails...");
    let sent = 0;
    for (const row of csvData) {
      const email = row["customer_email"] || row["email"];
      const name = row["customer_name"] || row["name"] || "";
      if (!email) continue;
      try {
        await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-review-request`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
          body: JSON.stringify({ customer_name: name, customer_email: email, business_name: businessName, google_review_link: googleLink, location_id: null }),
        });
        sent++;
      } catch (err) { console.error("Failed to send to", email); }
    }
    setSending(false);
    setCsvStatus(`Done — ${sent} review request emails sent successfully.`);
    setCsvData([]);
  }

  const now = new Date();
  const cutoff = new Date(now.getTime() - timeFilter * 24 * 60 * 60 * 1000);
  const filteredReviews = reviews.filter(r => new Date(r.created_at) >= cutoff);
  const filteredRequests = requests.filter(r => new Date(r.created_at) >= cutoff);
  const pendingReviews = reviews.filter(r => !r.replies?.[0] || r.replies?.[0]?.status !== "approved").length;
  const avgRating = filteredReviews.length ? (filteredReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / filteredReviews.length).toFixed(1) : "—";
  const approvedReplies = filteredReviews.filter(r => r.replies?.[0]?.status === "approved").length;
  const responseRate = filteredReviews.length ? Math.round((approvedReplies / filteredReviews.length) * 100) : 0;
  const conversionRate = filteredRequests.length ? Math.round((filteredReviews.length / filteredRequests.length) * 100) : 0;

  const ratingDistribution = [1, 2, 3, 4, 5].map(star => ({
    name: `${star}★`, count: filteredReviews.filter(r => r.rating === star).length,
    fill: star <= 2 ? "#ff4444" : star === 3 ? "#f5a623" : "#22c55e",
  }));

  const pieData = [
    { name: "Replied", value: approvedReplies, color: "#22c55e" },
    { name: "Pending", value: filteredReviews.length - approvedReplies, color: "#e0e0e0" },
  ];

  const trendData = (() => {
    const groups = {};
    filteredReviews.forEach(r => {
      const date = new Date(r.created_at);
      let key;
      if (timeFilter <= 7) key = date.toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" });
      else if (timeFilter <= 30) { const w = new Date(date); w.setDate(date.getDate() - date.getDay()); key = `Week of ${w.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`; }
      else key = date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      groups[key] = (groups[key] || 0) + 1;
    });
    return Object.entries(groups).map(([date, count]) => ({ date, count }));
  })();

  const ratingTrend = (() => {
    const groups = {};
    filteredReviews.forEach(r => {
      const date = new Date(r.created_at);
      let key;
      if (timeFilter <= 7) key = date.toLocaleDateString("en-US", { weekday: "short" });
      else if (timeFilter <= 30) { const w = new Date(date); w.setDate(date.getDate() - date.getDay()); key = w.toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
      else key = date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      if (!groups[key]) groups[key] = { total: 0, count: 0 };
      groups[key].total += r.rating || 0;
      groups[key].count += 1;
    });
    return Object.entries(groups).map(([date, { total, count }]) => ({ date, avg: parseFloat((total / count).toFixed(1)) }));
  })();

  const filtered = filter === "all" ? reviews : reviews.filter((r) => r.priority === filter);
  const counts = {
    all: reviews.length,
    red: reviews.filter((r) => r.priority === "red").length,
    yellow: reviews.filter((r) => r.priority === "yellow").length,
    green: reviews.filter((r) => r.priority === "green").length,
  };

  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: "#0f0f0f", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#888", fontFamily: "monospace" }}>Loading...</p>
    </div>
  );

  if (!session) return <LoginScreen onLogin={() => {}} />;
  if (isAdmin) return <AdminDashboard onLogout={handleLogout} />;

  return (
    <div style={{ fontFamily: "Georgia, serif", minHeight: "100vh", background: "#f9f9f7", padding: "0 0 80px" }}>
      <div style={{ background: "#0f0f0f", padding: isMobile ? "16px 20px" : "20px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ color: "#f5f0e8", fontSize: 22, margin: 0, fontWeight: 700 }}>Improve Review</h1>
          <p style={{ color: "#888", fontSize: 13, margin: "2px 0 0", fontStyle: "italic" }}>Review Management Dashboard</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ background: pendingReviews > 0 ? "#ff4444" : "#22c55e", borderRadius: 4, padding: "4px 12px", fontSize: 12, fontFamily: "monospace", fontWeight: 700, color: "#fff" }}>
            {pendingReviews} PENDING
          </div>
          <button onClick={handleLogout} style={{ background: "transparent", border: "1px solid #444", color: "#888", borderRadius: 4, padding: "4px 12px", fontSize: 12, fontFamily: "monospace", cursor: "pointer" }}>Sign Out</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e0e0e0", background: "#fff", padding: isMobile ? "0 12px" : "0 32px", overflowX: "auto" }}>
        {["reviews", "requests", "analytics"].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "12px 20px", border: "none", borderBottom: tab === t ? "2px solid #0f0f0f" : "2px solid transparent",
            background: "transparent", fontFamily: "monospace", fontSize: 13, fontWeight: 700,
            cursor: "pointer", color: tab === t ? "#0f0f0f" : "#888", textTransform: "uppercase", whiteSpace: "nowrap",
          }}>
            {t === "reviews" ? "Review Queue" : t === "requests" ? "Request Reviews" : "Analytics"}
          </button>
        ))}
      </div>

      <div style={{ padding: isMobile ? "16px 16px 0" : "24px 32px 0" }}>
        {tab === "reviews" && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
              {["all", "red", "yellow", "green"].map((f) => (
                <button key={f} onClick={() => setFilter(f)} style={{
                  padding: "6px 16px", borderRadius: 4, border: filter === f ? "2px solid #0f0f0f" : "1px solid #ddd",
                  background: filter === f ? "#0f0f0f" : "#fff", color: filter === f ? "#fff" : "#333",
                  fontFamily: "monospace", fontSize: 12, fontWeight: 700, cursor: "pointer", textTransform: "uppercase",
                }}>
                  {f === "all" ? `All (${counts.all})` : f === "red" ? `🔴 Urgent (${counts.red})` : f === "yellow" ? `🟡 Neutral (${counts.yellow})` : `🟢 Positive (${counts.green})`}
                </button>
              ))}
            </div>
            {loading && <p style={{ color: "#888", fontStyle: "italic" }}>Loading reviews...</p>}
            {!loading && filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa" }}>
                <p style={{ fontSize: 18 }}>No reviews yet</p>
                <p style={{ fontSize: 14 }}>Reviews will appear here automatically when they come in</p>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {filtered.map((review) => {
                const colors = PRIORITY_COLORS[review.priority] || PRIORITY_COLORS.green;
                const reply = review.replies?.[0];
                const isApproved = reply?.status === "approved";
                return (
                  <div key={review.id} style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 15, color: "#0f0f0f" }}>{review.reviewer_name}</span>
                        <span style={{ marginLeft: 10, fontSize: 13, color: "#888" }}>{"⭐".repeat(review.rating)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ background: colors.badge, color: "#fff", borderRadius: 3, padding: "2px 10px", fontSize: 11, fontFamily: "monospace", fontWeight: 700 }}>{colors.label}</span>
                        {isApproved && <span style={{ background: "#22c55e", color: "#fff", borderRadius: 3, padding: "2px 10px", fontSize: 11, fontFamily: "monospace", fontWeight: 700 }}>APPROVED</span>}
                      </div>
                    </div>
                    <p style={{ fontSize: 14, lineHeight: 1.6, color: "#333", margin: "0 0 16px", fontStyle: "italic" }}>"{review.review_text}"</p>
                    {reply && (
                      <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, padding: 14 }}>
                        <p style={{ fontSize: 11, fontFamily: "monospace", color: "#888", margin: "0 0 8px", textTransform: "uppercase", fontWeight: 700 }}>AI Draft Reply</p>
                        <textarea defaultValue={reply.edited_reply} onChange={(e) => updateReply(reply.id, e.target.value)} disabled={isApproved} rows={4}
                          style={{ width: "100%", border: "none", outline: "none", fontSize: 14, lineHeight: 1.6, fontFamily: "Georgia, serif", resize: "vertical", background: "transparent", color: "#333", boxSizing: "border-box" }} />
                        {!isApproved && (
                          <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                            <button onClick={() => copyToClipboard(reply.edited_reply, reply.id)} style={{ background: copied === reply.id ? "#22c55e" : "transparent", color: copied === reply.id ? "#fff" : "#888", border: "1px solid #ddd", borderRadius: 4, padding: "6px 14px", fontFamily: "monospace", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                              {copied === reply.id ? "Copied!" : "Copy"}
                            </button>
                            <button onClick={() => approveReply(reply.id)} style={{ background: "#22c55e", color: "#fff", border: "none", borderRadius: 4, padding: "6px 20px", fontFamily: "monospace", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                              ✓ Approve
                            </button>
                          </div>
                        )}
                        {isApproved && (
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                            <button onClick={() => copyToClipboard(reply.edited_reply, reply.id)} style={{ background: copied === reply.id ? "#22c55e" : "transparent", color: copied === reply.id ? "#fff" : "#888", border: "1px solid #ddd", borderRadius: 4, padding: "6px 14px", fontFamily: "monospace", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                              {copied === reply.id ? "✓ Copied!" : "Copy Reply"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "requests" && (
          <div style={{ maxWidth: 560 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>Request Reviews from Customers</h2>
            <p style={{ fontSize: 14, color: "#888", margin: "0 0 24px", fontStyle: "italic" }}>Upload a CSV of customer emails and we'll send them a review request automatically.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Business Name</label>
                <input type="text" placeholder="e.g. Casa Verde Kitchen" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                  style={{ width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: 4, fontSize: 14, fontFamily: "Georgia, serif", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Google Review Link</label>
                <input type="text" placeholder="https://g.page/r/your-review-link" value={googleLink} onChange={(e) => setGoogleLink(e.target.value)}
                  style={{ width: "100%", padding: "10px 14px", border: "1px solid #ddd", borderRadius: 4, fontSize: 14, fontFamily: "Georgia, serif", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Customer CSV</label>
                <p style={{ fontSize: 12, color: "#888", margin: "0 0 8px" }}>CSV must have columns: customer_name, customer_email</p>
                <input type="file" accept=".csv" onChange={handleCSV} style={{ fontSize: 14 }} />
              </div>
            </div>
            {csvStatus && <div style={{ background: "#f0fff4", border: "1px solid #b2f5c8", borderRadius: 4, padding: "10px 14px", fontSize: 14, color: "#333", marginBottom: 16 }}>{csvStatus}</div>}
            <button onClick={sendRequests} disabled={sending || !csvData.length} style={{
              width: "100%", background: sending || !csvData.length ? "#ddd" : "#0f0f0f",
              color: sending || !csvData.length ? "#999" : "#fff", border: "none", borderRadius: 4,
              padding: "13px", fontFamily: "monospace", fontWeight: 700, fontSize: 14,
              cursor: sending || !csvData.length ? "not-allowed" : "pointer",
            }}>
              {sending ? "Sending..." : "Send Review Requests"}
            </button>
          </div>
        )}

        {tab === "analytics" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Analytics Overview</h2>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {TIME_FILTERS.map(tf => (
                  <button key={tf.days} onClick={() => setTimeFilter(tf.days)} style={{
                    padding: "6px 14px", borderRadius: 4, border: timeFilter === tf.days ? "2px solid #0f0f0f" : "1px solid #ddd",
                    background: timeFilter === tf.days ? "#0f0f0f" : "#fff", color: timeFilter === tf.days ? "#fff" : "#333",
                    fontFamily: "monospace", fontSize: 11, fontWeight: 700, cursor: "pointer",
                  }}>{tf.label}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 32 }}>
              {[
                { label: "Total Reviews Managed", value: filteredReviews.length, color: "#0f0f0f", subtitle: "via Improve Review" },
                { label: "Average Rating", value: `${avgRating}★`, color: "#f5a623", subtitle: "of managed reviews" },
                { label: "Requests Sent", value: filteredRequests.length, color: "#4f46e5", subtitle: "review request emails sent" },
                { label: "Response Rate", value: `${responseRate}%`, color: "#22c55e", subtitle: "replies approved" },
                { label: "Conversion Rate", value: `${conversionRate}%`, color: "#06b6d4", subtitle: "requests that became reviews" },
              ].map((stat) => (
                <div key={stat.label} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: "20px 24px" }}>
                  <div style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{stat.label}</div>
                  <div style={{ fontSize: 36, fontWeight: 700, color: stat.color, letterSpacing: "-0.02em" }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: "#aaa", fontStyle: "italic", marginTop: 4 }}>{stat.subtitle}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginBottom: 32 }}>
              <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 24 }}>
                <p style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", color: "#555" }}>Review Volume Trend</p>
                <p style={{ fontSize: 11, color: "#aaa", fontStyle: "italic", marginBottom: 16 }}>{timeFilter <= 7 ? "Daily breakdown" : timeFilter <= 30 ? "Weekly breakdown" : "Monthly breakdown"}</p>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#0f0f0f" strokeWidth={2} dot={{ fill: "#f4e04d", strokeWidth: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p style={{ color: "#aaa", fontStyle: "italic", fontSize: 14, textAlign: "center", paddingTop: 60 }}>No data for this period</p>}
              </div>
              <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 24 }}>
                <p style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", color: "#555" }}>Rating Distribution</p>
                <p style={{ fontSize: 11, color: "#aaa", fontStyle: "italic", marginBottom: 16 }}>Breakdown by star rating</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ratingDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {ratingDistribution.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 24 }}>
                <p style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", color: "#555" }}>Response Rate</p>
                <p style={{ fontSize: 11, color: "#aaa", fontStyle: "italic", marginBottom: 16 }}>Replies approved vs pending</p>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} dataKey="value">
                      {pieData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <p style={{ textAlign: "center", fontFamily: "monospace", fontWeight: 700, fontSize: 24, margin: "-10px 0 0", color: "#22c55e" }}>{responseRate}%</p>
              </div>
              <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 24 }}>
                <p style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", color: "#555" }}>Rating Trend</p>
                <p style={{ fontSize: 11, color: "#aaa", fontStyle: "italic", marginBottom: 16 }}>Is your average rating improving over time?</p>
                {ratingTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={ratingTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis domain={[1, 5]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="avg" stroke="#f5a623" strokeWidth={2} dot={{ fill: "#f5a623", strokeWidth: 2 }} name="Avg Rating" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p style={{ color: "#aaa", fontStyle: "italic", fontSize: 14, textAlign: "center", paddingTop: 60 }}>No data for this period</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}